import { useEffect, useMemo, useState } from 'react'
import { Image as ImageIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReaderBundle } from '@/api/readerApi'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatCount } from '@/utils/format'
import { ReaderAudioBar } from './ReaderAudioBar'
import { ProseRenderer } from './ProseRenderer'
import { readerFontFamily, type ReaderSettings } from './readerSettings'

const CHAPTER_PAGE_BUDGET = {
  minChars: 1500,
  targetChars: 2200,
  maxChars: 3000,
}

interface ChapterPageBudget {
  minChars: number
  targetChars: number
  maxChars: number
}

function splitChapterParagraphs(content: string) {
  const trimmed = content.trim()
  if (!trimmed) return []

  const hasBlankLineBreak = /\n\s*\n/.test(trimmed)
  const chunks = hasBlankLineBreak ? trimmed.split(/\n\s*\n+/) : trimmed.split(/\n+/)
  return chunks.filter((chunk) => chunk.trim())
}

export function paginateChapterContent(content: string | null | undefined, budget: ChapterPageBudget = CHAPTER_PAGE_BUDGET) {
  const paragraphs = splitChapterParagraphs(content ?? '')
  if (!paragraphs.length) return ['']

  const pages: string[] = []
  let current: string[] = []
  let currentLength = 0

  for (const paragraph of paragraphs) {
    const separatorLength = current.length ? 2 : 0
    const nextLength = currentLength + separatorLength + paragraph.length

    if (current.length && currentLength >= budget.minChars && nextLength > budget.targetChars) {
      pages.push(current.join('\n\n'))
      current = [paragraph]
      currentLength = paragraph.length
      continue
    }

    if (current.length && nextLength > budget.maxChars) {
      pages.push(current.join('\n\n'))
      current = [paragraph]
      currentLength = paragraph.length
      continue
    }

    current.push(paragraph)
    currentLength = nextLength
  }

  if (current.length) pages.push(current.join('\n\n'))
  return pages.length ? pages : ['']
}

interface ChapterCanvasProps {
  bundle?: ReaderBundle
  isLoading: boolean
  settings: ReaderSettings
  isMobile: boolean
}

function ReaderAssetReviewBadge() {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center rounded-full border border-amber-300/70 bg-amber-100/80 px-2 py-0.5 text-xs font-medium text-amber-900">
      {t('status.asset.needs_review_attention')}
    </span>
  )
}

export function ChapterCanvas({ bundle, isLoading, settings, isMobile }: ChapterCanvasProps) {
  const { t } = useTranslation()
  const chapter = bundle?.chapter
  const headerImage = bundle?.assets.header_image
  const audio = bundle?.assets.audio
  const fontFamily = readerFontFamily(settings.fontFamily)
  const [page, setPage] = useState(0)
  const pages = useMemo(() => paginateChapterContent(chapter?.content), [chapter?.content])
  const safePage = Math.min(page, pages.length - 1)
  const showPagination = pages.length > 1

  useEffect(() => {
    setPage(0)
  }, [chapter?.id])

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[760px] space-y-5 px-4 py-8">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-[280px] w-full rounded-lg" />
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[92%]" />
          <Skeleton className="h-4 w-[96%]" />
          <Skeleton className="h-4 w-[88%]" />
        </div>
      </div>
    )
  }

  if (!chapter) {
    return (
      <div className="grid min-h-[calc(100vh-7rem)] place-items-center px-4 py-12">
        <div className="max-w-md text-center">
          <p className="text-lg font-semibold text-foreground">{t('reader.pickChapterTitle')}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t('reader.pickChapterDescription')}</p>
        </div>
      </div>
    )
  }

  return (
    <article
      className={cn(
        'min-h-[calc(100vh-7rem)] transition-colors',
        settings.theme === 'night'
          ? 'bg-[#11100e] text-[#e9dfcf]'
          : 'bg-[#f7f1e7] text-[#2f261d]',
        audio?.url && isMobile ? 'pb-24' : '',
      )}
    >
      <div className="mx-auto w-full max-w-[38em] px-5 py-8 sm:px-6 sm:py-12" style={{ fontFamily }}>
        <header className="mb-8 text-center">
          <p className="mb-2 text-xs text-muted-foreground">
            {t('reader.chapterNumber', { number: formatCount(chapter.chapter_no) })}
          </p>
          <h1 className="text-3xl font-semibold tracking-normal sm:text-4xl">{chapter.title || t('reader.untitled')}</h1>
          <p className="mt-3 text-sm text-muted-foreground">{t('reader.wordCount', { value: formatCount(chapter.word_count ?? 0) })}</p>
        </header>

        {headerImage?.url ? (
          <figure className="mb-9">
            <img
              src={headerImage.url}
              alt={headerImage.caption || t('reader.headerImageAlt')}
              className="max-h-[520px] w-full rounded-lg border object-cover shadow-sm"
              loading="lazy"
            />
            {(headerImage.caption || headerImage.status === 'needs_review') ? (
              <figcaption className="mt-2 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
                {headerImage.caption ? (
                  <>
                    <ImageIcon className="size-3.5" />
                    {headerImage.caption}
                  </>
                ) : null}
                {headerImage.status === 'needs_review' ? <ReaderAssetReviewBadge /> : null}
              </figcaption>
            ) : null}
          </figure>
        ) : null}

        <ProseRenderer
          content={pages[safePage] ?? ''}
          lang={bundle?.project.content_language}
          style={{
            fontSize: settings.fontSize,
            lineHeight: 1.8,
            fontFamily,
            color: 'inherit',
          }}
        />

        {showPagination ? (
          <nav className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-current/10 pt-5 sm:flex-row" aria-label={t('pagination.pageWithUnit', { page: safePage + 1, total: pages.length })}>
            <Button type="button" variant="outline" disabled={safePage === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>
              {t('pagination.previous')}
            </Button>
            <span className="nf-mono text-sm text-muted-foreground">
              {t('pagination.pageWithUnit', { page: safePage + 1, total: pages.length })}
            </span>
            <Button type="button" variant="outline" disabled={safePage >= pages.length - 1} onClick={() => setPage((value) => Math.min(pages.length - 1, value + 1))}>
              {t('pagination.next')}
            </Button>
          </nav>
        ) : null}

        {audio?.url && !isMobile ? (
          <div className="mt-10">
            {audio.status === 'needs_review' ? (
              <div className="mb-3 flex justify-start">
                <ReaderAssetReviewBadge />
              </div>
            ) : null}
            <ReaderAudioBar audioUrl={audio.url} isMobile={false} />
          </div>
        ) : null}
      </div>

      {audio?.url && isMobile ? (
        <div className="px-5 pb-3 sm:px-6">
          {audio.status === 'needs_review' ? (
            <div className="mb-3 flex justify-start">
              <ReaderAssetReviewBadge />
            </div>
          ) : null}
          <ReaderAudioBar audioUrl={audio.url} isMobile />
        </div>
      ) : null}
    </article>
  )
}
